import React, { useEffect, useMemo, useRef, useState } from "react";

const HIDDEN = new Set(["ZMAINTENANCE", "ZASSET"]);

/** Modern flat module glyphs (SVG), Microsoft-style colorful tiles */
const MODULE_META = {
  ZTASK: {
    label: "ZTask",
    color: "#2563eb",
    path: "/dashboard",
    icon: (
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true">
        <path d="M9 11l3 3L22 4" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
      </svg>
    ),
  },
  ZORDER: {
    label: "ZOrder",
    color: "#0d9488",
    path: "/order-dashboard",
    icon: (
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true">
        <path d="M9 5h12M9 12h12M9 19h12" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
        <circle cx="4" cy="5" r="1.6" fill="currentColor" />
        <circle cx="4" cy="12" r="1.6" fill="currentColor" />
        <circle cx="4" cy="19" r="1.6" fill="currentColor" />
      </svg>
    ),
  },
  ZINVENTORY: {
    label: "ZInventory",
    color: "#ea580c",
    path: "/",
    icon: (
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true">
        <path d="M21 8l-9-4-9 4v8l9 4 9-4V8z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        <path d="M3 8l9 4 9-4M12 12v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
  ZHR: {
    label: "ZHR",
    color: "#7c3aed",
    path: "/",
    icon: (
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true">
        <circle cx="9" cy="8" r="3.2" stroke="currentColor" strokeWidth="2" />
        <path d="M3.5 19c1.2-3.2 3.2-4.5 5.5-4.5s4.3 1.3 5.5 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <circle cx="17" cy="9" r="2.4" stroke="currentColor" strokeWidth="2" />
        <path d="M16 14.5c1.6 0 3 .7 4 2.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
  ZCOMPLIANCE: {
    label: "ZCompliance",
    color: "#dc2626",
    path: "/compliance",
    icon: (
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true">
        <path d="M12 3l8 3v6c0 5-3.4 8.4-8 9-4.6-.6-8-4-8-9V6l8-3z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  ZCUSTOMER: {
    label: "ZCustomer",
    color: "#0891b2",
    path: "/customers",
    icon: (
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true">
        <rect x="3" y="7" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="2" />
        <path d="M8 7V5a4 4 0 0 1 8 0v2" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
  },
};

function appKey(name) {
  return String(name || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "");
}

function stagingSibling(ztoolUrl, hostPrefix) {
  try {
    const u = new URL(ztoolUrl);
    u.hostname = u.hostname.replace(/^crane\./, `${hostPrefix}.`);
    return u.origin;
  } catch {
    return "";
  }
}

function resolveBase(key, ztool, moduleBases = {}) {
  if (moduleBases[key]) return String(moduleBases[key]).replace(/\/$/, "");

  const env = {
    ZTASK: process.env.REACT_APP_ZTASK_BASE_URL || process.env.REACT_APP_API_BASE_URL_FOR_ZORDER,
    ZORDER: process.env.REACT_APP_API_BASE_URL_FOR_ZORDER || process.env.REACT_APP_ZTASK_BASE_URL,
    ZHR: process.env.REACT_APP_ZHR_BASE_URL,
    ZINVENTORY: process.env.REACT_APP_API_BASE_URL_FOR_ZINVENTORY,
    ZCOMPLIANCE: process.env.REACT_APP_API_BASE_URL_FOR_ZCOMPLIANCE,
    ZCUSTOMER: process.env.REACT_APP_API_BASE_URL_FOR_ZCUSTOMER,
  };
  if (env[key]) return String(env[key]).replace(/\/$/, "");

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  if ((key === "ZTASK" || key === "ZORDER" || key === "ZCOMPLIANCE" || key === "ZCUSTOMER") && origin) {
    if (origin.includes("inventory") || origin.includes("hr")) {
      return stagingSibling(ztool, "crane-ztask") || origin;
    }
    return origin;
  }
  if (ztool) {
    if (key === "ZHR") return stagingSibling(ztool, "crane-hr") || ztool;
    if (key === "ZINVENTORY") return stagingSibling(ztool, "crane-inventory") || ztool;
    if (key === "ZTASK" || key === "ZORDER" || key === "ZCOMPLIANCE" || key === "ZCUSTOMER") {
      return stagingSibling(ztool, "crane-ztask") || ztool;
    }
  }
  return origin || ztool || "";
}

function buildLaunchUrl(key, baseUrl, path) {
  const token = localStorage.getItem("zTaskAuthToken") || "";
  const userDetailsString = localStorage.getItem("zTaskUserDetails") || "{}";
  let safeUserDetails = {};
  try {
    const parsed = JSON.parse(userDetailsString);
    safeUserDetails = {
      id: parsed.id,
      name: parsed.name,
      email: parsed.email,
      active: parsed.active,
      apps: parsed.apps,
      role: parsed.role,
    };
  } catch {
    /* ignore */
  }
  const cleanBase = String(baseUrl || "").replace(/\/$/, "");
  const encodedUserDetails = encodeURIComponent(JSON.stringify(safeUserDetails));
  return `${cleanBase}${path || "/"}#token=${encodeURIComponent(token)}&userDetails=${encodedUserDetails}&app=${encodeURIComponent(key)}`;
}

/**
 * Microsoft-style 9-dot module switcher for in-app headers.
 */
export function AppLauncher({
  currentApp = "",
  ztoolUrl = process.env.REACT_APP_ZTOOL_URL,
  moduleBases = {},
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const closeTimerRef = useRef(null);
  const ztool = String(ztoolUrl || "").replace(/\/$/, "");

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const openPanel = () => {
    clearCloseTimer();
    setOpen(true);
  };

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => setOpen(false), 180);
  };

  useEffect(() => () => clearCloseTimer(), []);

  const modules = useMemo(() => {
    let apps = [];
    try {
      const details = JSON.parse(localStorage.getItem("zTaskUserDetails") || "{}");
      apps = Array.isArray(details.apps) ? details.apps : [];
    } catch {
      apps = [];
    }
    const fromUser = apps
      .map((a) => {
        const key = appKey(a.appName || a.name);
        if (!key || HIDDEN.has(key) || !MODULE_META[key]) return null;
        const meta = MODULE_META[key];
        const baseUrl = resolveBase(key, ztool, moduleBases);
        if (!baseUrl) return null;
        return {
          key,
          label: meta.label,
          color: meta.color,
          icon: meta.icon,
          href: buildLaunchUrl(key, baseUrl, meta.path),
        };
      })
      .filter(Boolean);

    if (fromUser.length === 0 && currentApp && MODULE_META[appKey(currentApp)]) {
      const key = appKey(currentApp);
      const meta = MODULE_META[key];
      const baseUrl = resolveBase(key, ztool, moduleBases);
      if (baseUrl) {
        return [
          {
            key,
            label: meta.label,
            color: meta.color,
            icon: meta.icon,
            href: buildLaunchUrl(key, baseUrl, meta.path),
          },
        ];
      }
    }
    return fromUser;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentApp, ztool]);

  const filtered = modules.filter((m) =>
    !query.trim() ? true : m.label.toLowerCase().includes(query.trim().toLowerCase())
  );

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div
      className="crane-app-launcher"
      ref={rootRef}
      onMouseEnter={openPanel}
      onMouseLeave={scheduleClose}
    >
      <button
        type="button"
        className="crane-app-launcher__trigger"
        aria-label="Open module launcher"
        aria-expanded={open}
        title="Switch module"
        onClick={() => setOpen((v) => !v)}
        onFocus={openPanel}
      >
        <span className="crane-app-launcher__dots" aria-hidden="true">
          {Array.from({ length: 9 }).map((_, i) => (
            <i key={i} />
          ))}
        </span>
      </button>
      {open && (
        <div className="crane-app-launcher__panel" role="dialog" aria-label="Modules">
          <input
            className="crane-app-launcher__search"
            placeholder="Find apps…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={openPanel}
          />
          <div className="crane-app-launcher__grid">
            {filtered.map((m) => (
              <a
                key={m.key}
                href={m.href}
                className={`crane-app-launcher__tile${appKey(currentApp) === m.key ? " is-active" : ""}`}
                title={m.label}
                onClick={() => setOpen(false)}
              >
                <span className="crane-app-launcher__icon" style={{ background: m.color }}>
                  {m.icon}
                </span>
                <span className="crane-app-launcher__label">{m.label}</span>
              </a>
            ))}
            {ztool && (
              <a
                href={`${ztool}/dashboard`}
                className="crane-app-launcher__tile"
                title="All apps in ZTools"
                onClick={() => setOpen(false)}
              >
                <span className="crane-app-launcher__icon crane-app-launcher__icon--all">
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true">
                    <rect x="3" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2" />
                    <rect x="14" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2" />
                    <rect x="3" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2" />
                    <rect x="14" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2" />
                  </svg>
                </span>
                <span className="crane-app-launcher__label">All apps</span>
              </a>
            )}
          </div>
          {filtered.length === 0 && (
            <p className="crane-app-launcher__empty">No matching apps</p>
          )}
        </div>
      )}
    </div>
  );
}

export default AppLauncher;
